import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import itemsDataHelper from './paf-autocomplete.helper';
import inputEvents from '../paf-text-input/paf-text-input.events';
import { InterfacePafAutocomplete } from './paf-autocomplete.custom';

@Component({
  shadow: true,
  styleUrl: 'paf-autocomplete.scss',
  tag: 'paf-autocomplete',
})

export class PafAutocomplete implements ComponentInterface {

  /**
   * Items to show in the autocomplete interface. You should pass a stringified
   * array of objects, containing the `name` and `id` key
   * for each object with an appropriate value -> [InterfacePlaces].
   */
  @Prop() public items?: string;

  /**
   * The value to use as default value for the input. The input value or the
   * selected autocomplete value is reflected to this attribute.
   */
  @Prop() public value?: string;

  /**
   * Autocomplete id. If you use multiple instances on a page,
   * you should set individual id's to each of them.
   */
  @Prop() public autocompleteId? = 'autocomplete-list';

  /**
   * a11y text as hint that users can navigate the list with the arrow keys
   */
  @Prop() public helpTextUseArrowKeysToNavigate!: string;

  /**
   * a11y text to announce the total number of autocomplete items.
   * You should provide the placeholder {x} inside of this string.
   * We then replace {x} by the amount of autocomplete results.
   */
  @Prop() public helpTextResultsAvailable!: string;

  /**
   * Name attribute for the input element. See text-input for details.
   */
  @Prop() public inputName!: string;

  /**
   * Label attribute for the input element. See text-input for details.
   */
  @Prop() public inputLabel!: string;

  /**
   * Placeholder attribute for the input element. See text-input for
   * details.
   */
  @Prop() public inputPlaceholder?: string;

  /**
   * The error message to show if the input field is invalid
   */
  @Prop() public inputErrorMessage?: string;

  /**
   * Define if the input is required
   */
  @Prop() public inputRequired = true;

  /**
   * The hidden title text to use for the autocomplete list
   */
  @Prop() public listAssistiveText?: string;

  /**
   * Property to indicate if user has selected an item.
   * Background: if user has selected an item and presses return key,
   * we need to have this information in timetable, so that we can prevent
   * issuing a search request in this scenario
   */
  @Prop({
    mutable: true,
    reflect: true,
  }) public itemSelected = false;

  // @State() private _inputValue: string;
  @State() private _isVisible = false;
  @State() private _selectedAutocompleteItemIndex = -1;
  @State() private _dataItems?: [InterfacePafAutocomplete['item']];
  @State() private _inputValue: string;

  @Listen('demand-focus')
  public handleFocusDemandFromOutside(): void {
    this._inputElement.dispatchEvent(new CustomEvent('demand-focus'));
  }

  /**
   * Dispatched if an autocomplete item is selected
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-autocomplete_selected',
  })
  public selectedEvent: EventEmitter<InterfacePafAutocomplete['item']>;

  @Watch('items')
  public handleItemsChange(newValue: string): void {
    if (newValue && newValue.length > 0) {
      this._dataItems = itemsDataHelper(this.items);

      if (this._dataItems && this._dataItems.length > 0) {
        this._isVisible = true;
      }
    } else {
      this._dataItems = undefined;
    }
  }

  @Watch('value')
  public handleValueChange(newValue: string): void {
    this._initialInputValue = newValue;
    this._inputValue = newValue;
  }

  private _nativeInputElement!: HTMLInputElement;
  private _inputElement!: HTMLPafTextInputElement;
  private _list!: HTMLUListElement;
  private _userDidManipulateInputValue = false;
  private _initialInputValue = '';
  private _emittedEventPayload = '';

  /**
   * ------------------------------------------------------------------------
   * key handling
   * ------------------------------------------------------------------------
   */
  private _handleArrowKeys = (key): void => {
    if (!this._dataItems || this._dataItems.length < 1) {
      return;
    }

    this._isVisible = true;

    const isDownKey = key === 'ArrowDown';

    if (isDownKey) {
      if (this._selectedAutocompleteItemIndex < this._dataItems.length - 1) {
        this._selectedAutocompleteItemIndex++;
      } else {
        this._selectedAutocompleteItemIndex = 0;
      }
    } else {
      if (this._selectedAutocompleteItemIndex > 0) {
        this._selectedAutocompleteItemIndex--;
      } else {
        this._selectedAutocompleteItemIndex = this._dataItems.length - 1;
      }
    }

    this._inputValue = this._dataItems[this._selectedAutocompleteItemIndex].name;

    const selectedElement =
    this._list.children[this._selectedAutocompleteItemIndex];

    selectedElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    this.itemSelected = this._selectedAutocompleteItemIndex !== -1;

  };

  private _handleEscapeKey = (): void => {

    this.itemSelected = false;

    // we don't want to handle esc-key if the user did not type anything
    if (this._userDidManipulateInputValue) {
      if (this._initialInputValue && this._initialInputValue.length > 0) {
        this._inputValue = this._initialInputValue;
      } else {
        this._inputValue = '';
      }
    }

    this._isVisible = false;
    this._userDidManipulateInputValue = false;
  };

  private _handleEnterKey = (): void => {
    if (this._selectedAutocompleteItemIndex === -1) {
      return;
    }

    const item = this._dataItems[this._selectedAutocompleteItemIndex];

    if (!item) {
      return;
    }

    this._inputValue = item.name;
    this._initialInputValue = this._inputValue;
    this._selectedAutocompleteItemIndex = -1;
    this.itemSelected = false;

    this._emitSelectedEventWithId();
  };

  private _handleKeyPress = (evt: KeyboardEvent): void => {
    const {
      key,
    } = evt;

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      this._handleArrowKeys(key);

      return;
    }

    if (key === 'Escape') {
      this._handleEscapeKey();

      return;
    }

    if (key === 'Enter') {
      this._handleEnterKey();
    }
  };

  private _focusInputElement = (): void => {
    const event = new CustomEvent('focus', {
      bubbles: false,
      composed: false,
    });

    /**
     * paf-text-input listens to this event
     */
    this._nativeInputElement.dispatchEvent(event);
  };

  private _handleInput = (): void => {
    if (this._isVisible) {
      this._selectedAutocompleteItemIndex = -1;
      this.itemSelected = false;
    }
  };

  /**
   * ------------------------------------------------------------------------
   * User interaction
   * ------------------------------------------------------------------------
   */

  private _emitSelectedEventWithId = (): void => {

    /**
     * consumers of paf-autocomplete may listen to this event
     * This event only fires, if the user did select an autocomplete-item.
     * It does not fire by the blur event of the input field.
     * Thus, the payload will contain both a value and an id.
     */
    const searchItem = this._dataItems.filter((item) => item.name === this._inputValue);

    if (searchItem.length === 1) {
      if (this._emittedEventPayload !== searchItem[0].name) {
        this.selectedEvent.emit({
          id: searchItem[0].id,
          name: searchItem[0].name,
        });
      }
    }

    this._focusInputElement();
    this._isVisible = false;
    this._userDidManipulateInputValue = false;
    this._emittedEventPayload = searchItem[0].name;
  };

  private _handleFocus = (): void => {
    this._isVisible = this._dataItems?.length > 0;
  };

  private _handleBlur = (evt): void => {

    /**
     * tricky:
     * in the event chain, blur comes before click. As soon as a user
     * clicks on a list-item (and these are outside of the input area, thus
     * triggering a blur event), we never get a click event since at that
     * point in time the list has already been hidden by this blur handler.
     * To get around the issue, we check the event's relatedTarget to see
     * if the autocomplete item is contained in it.
     */
    const didClickList = this._list.contains(evt.relatedTarget);

    if (didClickList) {
      return;
    }

    this._isVisible = false;

    this._initialInputValue = this._inputValue;
    this._userDidManipulateInputValue = false;

    /**
     * consumers of paf-autocomplete may listen to this event
     * This event only fires by the blur event of input field.
     * It does not fire, if the user clicked an autocomplete item.
     * Thus, the payload will contain only a value but not an id.
     */
    if (this._nativeInputElement.value?.length > 0) {
      if (this._emittedEventPayload !== this._nativeInputElement.value) {
        this.selectedEvent.emit({
          id: false,
          name: this._nativeInputElement.value,
        });
      }
    }

    this._emittedEventPayload = this._nativeInputElement.value;
  };

  private _handleListClick = (evt): void => {
    const path = evt.composedPath();

    let firstElement = null;
    let firstLiElement = null;

    if (path.length > 0) {
      [firstElement] = path;
    }

    if (firstElement) {
      if (firstElement.tagName.toLowerCase() === 'li') {
        firstLiElement = firstElement;
      } else if (firstElement.tagName.toLowerCase() === 'ul') {
        firstLiElement = firstElement.firstChild;
      }
    }

    if (firstLiElement) {
      this._inputValue = firstLiElement.innerText;
      this._initialInputValue = this._inputValue;
      this._emitSelectedEventWithId();
    }

  };

  /**
   * ------------------------------------------------------------------------
   * Component lifecycle
   * ------------------------------------------------------------------------
   */

  public componentWillLoad(): void {
    this._dataItems = itemsDataHelper(this.items);
    this._initialInputValue = this.value;
    this._inputValue = this.value;
  }

  public componentDidLoad(): void {
    this._nativeInputElement = this._inputElement.querySelector('input');

    this._nativeInputElement.addEventListener('focus', this._handleFocus);
    this._list.addEventListener('click', this._handleListClick);
    this._nativeInputElement.addEventListener('blur', this._handleBlur);
    this._inputElement.addEventListener(inputEvents.inputEvent, this._handleInput);
    this._nativeInputElement.addEventListener('keydown', this._handleKeyPress);
  }

  public disconnectCallback(): void {
    this._nativeInputElement.removeEventListener('focus', this._handleFocus);
    this._list.removeEventListener('click', this._handleListClick);
    this._nativeInputElement.removeEventListener('blur', this._handleBlur);
    this._inputElement.removeEventListener(inputEvents.inputEvent, this._handleInput);
    this._nativeInputElement.removeEventListener('keydown', this._handleKeyPress);
  }

  /**
   * ------------------------------------------------------------------------
   * render helpers
   * ------------------------------------------------------------------------
   */
  private _listAttributes = (): any => {
    const listAttributes = {};

    if (this.autocompleteId) {
      listAttributes['id'] = this.autocompleteId;
    }

    if (!this._isVisible) {
      listAttributes['aria-hidden'] = 'true';
      listAttributes['role'] = 'presentation';
    }

    return listAttributes;
  };

  private _a11yHelpText = (): string => {
    let a11yHintText = '';

    if (this._isVisible && this._dataItems) {
      const a11yResults = this.helpTextResultsAvailable.replace('{x}', this._dataItems.length.toString());

      a11yHintText = `${a11yResults} ${this.helpTextUseArrowKeysToNavigate}`;
    }

    return a11yHintText;
  };

  /**
   * ------------------------------------------------------------------------
   * render
   * ------------------------------------------------------------------------
   */

  public render(): HTMLDivElement {
    return (
      <div class='autocomplete'>

        <paf-text-input
          autoCompleteIndex={this._selectedAutocompleteItemIndex === -1
            ? ''
            : `list-${this.inputName}-item-${this._selectedAutocompleteItemIndex}`
          }
          name={this.inputName}
          label={this.inputLabel}
          placeholder={this.inputPlaceholder}
          value={this._inputValue}
          inputId={`${this.autocompleteId}-input`}
          type='text'
          required={this.inputRequired}
          errorMessage={this.inputErrorMessage}
          ref={(elem) => {
            this._inputElement = elem;
          }}
        />

        <p
          class='accessibility-hint'
          aria-live='assertive'
          role='region'
          tabindex='-1'
          data-testid='a11yText'
          title={`${this.inputLabel} info`}
        >{this._a11yHelpText()}</p>

        <ul
          class={{
            'list': true,
            'list--visible': this._isVisible,
          }}
          role='listbox'
          {...this._listAttributes()}
          title={this.listAssistiveText}
          ref={(el): void => {
            this._list = el;
          }}
          data-testid='list'
        >
          {this._dataItems && this._dataItems.map((item, index) => (
            <paf-autocomplete-item
              itemId={`list-${this.inputName}-item-${index}`}
              text={item.name}
              selected={index === this._selectedAutocompleteItemIndex}
              ariaPosinset={index + 1}
              ariaSetsize={this._dataItems.length}
            />
          ))}
        </ul>

      </div>
    );
  }
}
